import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDownLeft, ArrowLeft, ArrowUpRight, Check, Copy, Search, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  type AccountRef,
  type AdminTxn,
  type Exposure,
  ExposureFrozen,
  Unauthorized,
  listAccounts,
  listTransactions,
  setExposure,
} from '@/lib/api';
import { formatAmount } from '@/lib/format';
import { dateParam, readParams, writeParams } from '@/lib/url';

const STATES: Exposure[] = ['undecided', 'included', 'excluded'];

const STATE_BADGE: Record<Exposure, 'secondary' | 'default' | 'outline'> = {
  undecided: 'secondary',
  included: 'default',
  excluded: 'outline',
};

/** Row-2 minimal decision surface: review one exposure state (with a booking-date
 * filter + paging) and include/exclude each transaction. The full multi-label grid
 * + cross-bank views are row 5. */
export function ExposureReview({
  onAuthError,
  source = null,
  title = 'Transactions',
  onBack,
}: {
  onAuthError: () => void;
  source?: string | null; // bank filter (null = all banks)
  title?: string;
  onBack?: () => void;
}) {
  const initial = useMemo(() => readParams(), []);
  const [state, setState] = useState<Exposure>(() => {
    const value = initial.get('exposure');
    return STATES.includes(value as Exposure) ? (value as Exposure) : 'undecided';
  });
  const [account, setAccount] = useState<number | null>(() => {
    const value = Number(initial.get('account'));
    return Number.isInteger(value) && value > 0 ? value : null;
  });
  const [accounts, setAccounts] = useState<AccountRef[]>([]);
  const [from, setFrom] = useState(() => dateParam(initial, 'from'));
  const [to, setTo] = useState(() => dateParam(initial, 'to'));
  const [query, setQuery] = useState(() => (initial.get('q') ?? '').slice(0, 140));
  const [search, setSearch] = useState(() => (initial.get('q') ?? '').slice(0, 140).trim());
  const [txns, setTxns] = useState<AdminTxn[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<number | null>(null);
  // Monotonic request id: a reset (tab/date change) bumps it, so a slower in-flight
  // request (e.g. a "Load more" started under the previous filter) is ignored when
  // it resolves — otherwise its page could append onto / clobber the new filter's
  // list and cursor (adversarial pre-pass finding).
  const reqSeq = useRef(0);

  // load(reset): reset replaces the list (state/filter change); otherwise appends
  // the next page. Keyset cursor is a single-traversal window (see API contract).
  const load = useCallback(
    async (reset: boolean) => {
      const seq = ++reqSeq.current;
      setLoading(true);
      setError(null);
      if (reset) {
        // Clear BEFORE fetching so a failed reset can't leave the previous
        // filter's rows on screen with a now-actionable stale cursor (Codex).
        setTxns([]);
        setNextCursor(null);
      }
      try {
        const page = await listTransactions({
          exposure: search ? 'all' : state,
          q: search || undefined,
          source: source ?? undefined,
          account: account ?? undefined,
          from: from || undefined,
          to: to || undefined,
          cursor: reset ? undefined : (nextCursor ?? undefined),
        });
        if (seq !== reqSeq.current) return; // a newer request superseded this one
        setTxns((prev) => (reset ? page.items : [...prev, ...page.items]));
        setNextCursor(page.nextCursor);
      } catch (e) {
        if (seq !== reqSeq.current) return;
        if (e instanceof Unauthorized) {
          onAuthError();
          return;
        }
        setError('Could not load transactions.');
      } finally {
        if (seq === reqSeq.current) setLoading(false);
      }
    },
    [state, source, account, from, to, search, nextCursor, onAuthError],
  );

  useEffect(() => {
    writeParams(
      {
        view: 'transactions',
        bank: source,
        exposure: state === 'undecided' ? null : state,
        account: account ? String(account) : null,
        from,
        to,
        q: search,
      },
      'replace',
    );
  }, [source, state, account, from, to, search]);

  // Search as you type, settling 300 ms after the last keystroke.
  useEffect(() => {
    const id = setTimeout(() => setSearch(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);

  // Reload from the top whenever the bank, state tab, account, or date range changes.
  useEffect(() => {
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, source, account, from, to, search]);

  // Load the account list once for the filter dropdown.
  useEffect(() => {
    listAccounts()
      .then(setAccounts)
      .catch((e) => {
        if (e instanceof Unauthorized) onAuthError();
      });
  }, [onAuthError]);

  // When viewing one bank, scope the account dropdown to that bank's accounts.
  const shownAccounts = useMemo(
    () => (source ? accounts.filter((a) => a.source === source) : accounts),
    [accounts, source],
  );

  // An account from the URL that this bank doesn't hold would filter to nothing: drop it.
  useEffect(() => {
    if (account !== null && accounts.length > 0 && !shownAccounts.some((a) => a.account_id === account)) {
      setAccount(null);
    }
  }, [account, accounts, shownAccounts]);

  async function decide(id: number, decision: 'included' | 'excluded') {
    setPending(id);
    try {
      await setExposure(id, decision);
      setTxns((prev) => prev.filter((t) => t.id !== id)); // leaves the current queue
    } catch (e) {
      if (e instanceof Unauthorized) {
        onAuthError();
        return;
      }
      if (e instanceof ExposureFrozen) {
        // Rev 5: surface the cutover freeze explicitly — this is expected state
        // during the WP-11 cutover window, not a save failure.
        setError(
          'Exposure decisions are frozen during the bank-cutover window. ' +
            'Try again after the cutover completes.',
        );
        return;
      }
      setError('Could not save the decision.');
    } finally {
      setPending(null);
    }
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        {onBack && (
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
            <ArrowLeft className="size-4" aria-hidden /> Banks
          </Button>
        )}
        <h1 className="text-xl font-semibold sm:text-2xl">{title}</h1>
        <div className="ml-auto flex gap-1" role="tablist" aria-label="Exposure state">
          {STATES.map((s) => (
            <Button
              key={s}
              size="sm"
              variant={s === state && !search ? 'default' : 'ghost'}
              aria-pressed={s === state && !search}
              disabled={!!search}
              onClick={() => setState(s)}
            >
              {s}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex min-w-64 flex-1 flex-col gap-1 text-xs text-muted-foreground">
          Search
          <span className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2" aria-hidden />
            <Input
              type="text"
              inputMode="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Transaction ID, reference or description"
              maxLength={140}
              className="pl-9 pr-9"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded p-1 hover:bg-muted"
                aria-label="Clear search"
              >
                <X className="size-4" aria-hidden />
              </button>
            )}
          </span>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Account
          <select
            value={account ?? ''}
            onChange={(e) => setAccount(e.target.value ? Number(e.target.value) : null)}
            className="h-10 w-64 rounded-md border border-input bg-background px-3 text-sm text-foreground"
          >
            <option value="">All accounts</option>
            {shownAccounts.map((a) => (
              <option key={a.account_id} value={a.account_id}>
                {a.provider_id}
                {a.bic ? ` · ${a.bic}` : ''} ({a.source.toUpperCase()})
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          From (booking date)
          <Input
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => setFrom(e.target.value)}
            className="w-40"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          To
          <Input
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.target.value)}
            className="w-40"
          />
        </label>
        {(from || to) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFrom('');
              setTo('');
            }}
          >
            Clear dates
          </Button>
        )}
      </div>
      {search && (
        <p className="text-xs text-muted-foreground">
          Searching every state (undecided, included and excluded) for “{search}”.
        </p>
      )}
      {(from || to) && (
        <p className="text-xs text-muted-foreground">
          A date filter excludes transactions with no booking date. Clear the dates to see them.
        </p>
      )}

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {txns.length === 0 && !loading ? (
        <p className="text-sm text-muted-foreground">
          {search ? `No transaction matches “${search}”.` : `Nothing in “${state}”.`}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {txns.map((t) => (
            <li key={t.id}>
              <TxnCard t={t} pending={pending === t.id} onDecide={decide} />
            </li>
          ))}
        </ul>
      )}

      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {nextCursor && !loading && (
        <Button variant="outline" onClick={() => load(false)} className="self-center">
          Load more
        </Button>
      )}
    </section>
  );
}

function oneLine(text: string | null): string {
  return text ? text.replace(/\s+/g, ' ').trim() : '';
}

function CopyId({ value }: { value: string }) {
  const [copied, setCopied] = useState<'yes' | 'failed' | null>(null);
  const flash = (result: 'yes' | 'failed') => {
    setCopied(result);
    setTimeout(() => setCopied(null), 1500);
  };
  return (
    <button
      type="button"
      title={copied === 'failed' ? 'Copy failed' : 'Copy transaction ID'}
      onClick={() => {
        if (!navigator.clipboard) {
          flash('failed');
          return;
        }
        navigator.clipboard.writeText(value).then(
          () => flash('yes'),
          () => flash('failed'),
        );
      }}
      className="group inline-flex cursor-pointer items-center gap-1 rounded px-1 font-mono text-foreground hover:bg-muted"
    >
      {value}
      {copied === 'yes' ? (
        <Check className="size-3.5 text-success" aria-hidden />
      ) : (
        <Copy
          className={`size-3.5 ${copied === 'failed' ? 'text-destructive' : 'text-muted-foreground group-hover:text-foreground'}`}
          aria-hidden
        />
      )}
      <span className="sr-only" aria-live="polite">
        {copied === 'yes' ? 'Copied' : copied === 'failed' ? 'Copy failed' : 'Copy'}
      </span>
    </button>
  );
}

function TxnCard({
  t,
  pending,
  onDecide,
}: {
  t: AdminTxn;
  pending: boolean;
  onDecide: (id: number, decision: 'included' | 'excluded') => void;
}) {
  const credit = Number(t.signed_amount) >= 0;
  const description = oneLine(t.narrative);
  const endToEnd = t.end_to_end_id && t.end_to_end_id !== 'NONREF' ? t.end_to_end_id : null;
  return (
    <article
      className={`rounded-lg border border-l-2 bg-card px-5 py-4 text-card-foreground shadow-xs transition-shadow duration-150 hover:shadow-sm ${
        credit ? 'border-l-success/60' : 'border-l-muted-foreground/30'
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span
          className={`inline-flex items-center gap-1.5 text-xs font-medium ${
            credit ? 'text-success' : 'text-muted-foreground'
          }`}
        >
          <span
            className={`flex size-5 items-center justify-center rounded-full ${
              credit ? 'bg-success/10' : 'bg-muted'
            }`}
          >
            {credit ? (
              <ArrowDownLeft className="size-3.5" aria-hidden />
            ) : (
              <ArrowUpRight className="size-3.5" aria-hidden />
            )}
          </span>
          {credit ? 'Credit' : 'Debit'}
        </span>
        <span className={`text-base font-semibold tabular-nums ${credit ? 'text-success' : 'text-foreground'}`}>
          {formatAmount(t.signed_amount.replace(/^-/, ''), t.currency)}
        </span>
        <Badge variant={STATE_BADGE[t.exposure]}>{t.exposure}</Badge>
        <div className="ml-auto flex gap-1.5">
          <Button
            size="sm"
            variant="default"
            className="h-7 px-3 text-xs"
            disabled={pending || t.exposure === 'included'}
            onClick={() => onDecide(t.id, 'included')}
          >
            Include
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-3 text-xs"
            disabled={pending || t.exposure === 'excluded'}
            onClick={() => onDecide(t.id, 'excluded')}
          >
            Exclude
          </Button>
        </div>
      </div>

      <dl className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground">
        <div className="flex gap-1">
          <dt>Booked</dt>
          <dd className="tabular-nums text-foreground">{t.booking_date ?? '—'}</dd>
        </div>
        {t.value_date && t.value_date !== t.booking_date && (
          <div className="flex gap-1">
            <dt>Value</dt>
            <dd className="tabular-nums text-foreground">{t.value_date}</dd>
          </div>
        )}
        <div className="flex items-center gap-1">
          <dt>Transaction ID</dt>
          <dd>{t.provider_txn_ref ? <CopyId value={t.provider_txn_ref} /> : '—'}</dd>
        </div>
        {endToEnd && (
          <div className="flex gap-1">
            <dt>End-to-end</dt>
            <dd className="font-mono text-foreground">{endToEnd}</dd>
          </div>
        )}
      </dl>

      {description && <p className="mt-2 break-words text-sm leading-snug">{description}</p>}

      <p className="mt-1.5 text-xs text-muted-foreground">
        <span className="font-mono">{t.account_provider_id}</span>
        {t.account_bic ? ` · ${t.account_bic}` : ''} · {t.source.toUpperCase()}
        {t.bank_txn_code ? ` · ${t.bank_txn_code}` : ''}
      </p>
    </article>
  );
}
